// src/context/CartContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Generate or retrieve sessionID
  const getSessionID = () => {
    let sid = localStorage.getItem('sessionID');
    if (!sid) {
      sid = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('sessionID', sid);
    }
    return sid;
  };

  const fetchCart = useCallback(async () => {
    try {
      const sessionID = getSessionID();
      const { data } = await axios.get(`${API_BASE_URL}/cart`, {
        headers: { 'Session-ID': sessionID },
      });
      setCart(data.items || []);
    } catch (err) {
      console.error('Fetch cart error:', err);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

// inside CartContext.js
const addToCart = async (product, quantity = 1, unitPrice = null) => {
  try {
    const sessionID = getSessionID();

    const productId = typeof product === 'string'
      ? product
      : (product && (product._id || product.productId || product.id));

    if (!productId) {
      return { success: false, message: 'Invalid product' };
    }

    const stock = product.stock ?? 0;
    if (stock <= 0) {
      return { success: false, message: 'Out of stock' };
    }

    const existing = cart.find(item => {
      const itemId = item.product?._id || item.productId || item._id;
      return itemId?.toString() === productId.toString();
    });

    const currentQty = existing ? existing.quantity : 0;
    const newQty = Math.min(currentQty + quantity, stock);

    if (newQty === currentQty) {
      return { success: false, message: 'Stock limit reached' };
    }

    await axios.post(
      `${API_BASE_URL}/cart/add`,
      { productId, quantity: newQty - currentQty, unitPrice },
      { headers: { 'Session-ID': sessionID } }
    );

    await fetchCart();
    return { success: true, message: 'Added to cart' };

  } catch (err) {
    console.error('Add to cart error:', err);
    return { success: false, message: 'Error adding to cart' };
  }
};




const updateQuantity = async (productId, quantity) => {
  try {
    const sessionID = getSessionID();

    // find product in cart
    const item = cart.find(i =>
      (i.productId?._id || i.productId)?.toString() === productId.toString()
    );

    if (!item) return;

    const stock = item.productId?.stock ?? Infinity;
    if (quantity > stock) {
      console.warn("Stock limit reached");
      return; // block request
    }

    await axios.put(
      `${API_BASE_URL}/cart/update`,
      { productId, quantity },
      { headers: { 'Session-ID': sessionID } }
    );
    fetchCart();
  } catch (err) {
    console.error('Update cart error:', err);
  }
};


  const removeFromCart = async (productId) => {
    try {
      const sessionID = getSessionID();
      await axios.delete(`${API_BASE_URL}/cart/remove/${productId}`, {
        headers: { 'Session-ID': sessionID },
      });
      fetchCart();
    } catch (err) {
      console.error('Remove from cart error:', err);
    }
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, updateQuantity, removeFromCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
